<template>
  <div>
    <scraping-core ref="scrapingCore" :format="format" :data="data" :client="client" :datasource="ds" />

    <b-modal v-model="dataModal" title="Données du produit" size="lg" ok-only ok-title="Fermer">
      <div v-if="dataModalRow">
        <div class="mb-2">
          <span class="badge badge-secondary mr-1">{{ getNomRow(dataModalRow) }}</span>
        </div>
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr><th style="width:40%">Colonne</th><th>Valeur</th></tr>
          </thead>
          <tbody>
            <tr v-for="(val, key) in dataModalFields" :key="key">
              <td class="font-weight-bold">{{ key }}</td>
              <td style="word-break:break-all">{{ val }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  builder_info: 'Interface de suivi scraping produits: filtres, configuration des scrapers, lancement de recherche URL et validation des résultats par domaine/type.',
  props: ['format', 'data', 'client'],

  data() {
    return {
      dataModal: false,
      dataModalRow: null,
    };
  },

  computed: {
    dataModalFields() {
      if (!this.dataModalRow) return {};
      return this._buildProductData(this.dataModalRow, this.$refs.scrapingCore?.categoriesMap || {});
    },

    ds() {
      return {
        // Toutes les valeurs PS sont les valeurs par défaut de scraping_core.vue
        // On définit explicitement les fonctions qui ne peuvent pas être JSON-sérialisées
        getNom: (product, lang) => {
          try {
            const nom = typeof product.nom === 'string' ? JSON.parse(product.nom) : product.nom;
            return nom?.[lang] || nom?.fr || product.reference || '—';
          } catch { return product.reference || '—'; }
        },
        getCleProduir: (product) => product.id_cms || '',
        selectFields: 'id, id_cms, status, reference, nom, marque, ean13, prix_ht, prix_ttc, prix_promo_ht, prix_promo_ttc, prix_achat, quantite, poids, largeur, hauteur, profondeur, devise, categories, default_category, caracteristiques',
        selectFieldsJoined: 'produits.id, produits.id_cms, produits.status, produits.reference, produits.nom, produits.marque, produits.ean13, produits.prix_ht, produits.prix_ttc, produits.prix_promo_ht, produits.prix_promo_ttc, produits.prix_achat, produits.quantite, produits.poids, produits.largeur, produits.hauteur, produits.profondeur, produits.devise, produits.categories, produits.default_category, produits.caracteristiques',
        getModalProductData: (product, catsMap) => this._buildProductData(product, catsMap || {}),
        rowActions: [
          { key: 'view-data', icon: 'fas fa-table', label: 'Voir les données', variant: 'outline-info', onClick: (row) => { this.dataModalRow = row; this.dataModal = true; } },
        ],
      };
    },
  },

  methods: {
    getNomRow(row) {
      try {
        const nom = typeof row.nom === 'string' ? JSON.parse(row.nom) : row.nom;
        return nom?.fr || nom?.gb || row.reference || '—';
      } catch { return row.reference || '—'; }
    },

    _buildProductData(product, catsMap = {}) {
      const LABELS = {
        id: 'id', id_cms: 'id_cms', status: 'Statut', reference: 'Référence',
        marque: 'Marque', ean13: 'EAN13',
        prix_ht: 'Prix HT', prix_ttc: 'Prix TTC',
        prix_promo_ht: 'Promo HT', prix_promo_ttc: 'Promo TTC',
        prix_achat: "Prix d'achat", quantite: 'Quantité',
        poids: 'Poids', largeur: 'Largeur', hauteur: 'Hauteur', profondeur: 'Profondeur',
        devise: 'Devise',
      };
      const SKIP = new Set(['nom', 'categories', 'default_category', 'caracteristiques', '__ob__']);
      const out = {};

      // Nom (JSON multilangue)
      try {
        const nom = typeof product.nom === 'string' ? JSON.parse(product.nom) : product.nom;
        if (nom) out['Nom'] = nom.fr || nom.gb || Object.values(nom)[0] || '';
      } catch (e) {}

      // Colonnes scalaires avec labels
      for (const [k, v] of Object.entries(product)) {
        if (SKIP.has(k) || k.startsWith('_')) continue;
        out[LABELS[k] || k] = v ?? '';
      }

      // Catégories (JSON array d'ids → noms)
      if (product.categories) {
        try {
          const cats = typeof product.categories === 'string' ? JSON.parse(product.categories) : product.categories;
          out['Catégories'] = Array.isArray(cats) ? cats.map(id => catsMap[id] || id).join(', ') : cats;
        } catch (e) { out['Catégories'] = product.categories; }
      }
      if (product.default_category != null) {
        out['Catégorie principale'] = catsMap[product.default_category] || product.default_category;
      }

      // Caractéristiques : une ligne par caract
      if (product.caracteristiques) {
        try {
          const caracts = typeof product.caracteristiques === 'string'
            ? JSON.parse(product.caracteristiques)
            : product.caracteristiques;
          for (const c of (caracts || [])) {
            const name = c.feature_name?.fr || c.feature_name?.gb || c.id_feature;
            const value = c.feature_value?.fr || c.feature_value?.gb || '';
            out['📋 ' + name] = value;
          }
        } catch (e) {}
      }

      return out;
    },
  },
};
</script>
